import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchHeadlines } from '../../../actions/news';
import useActions from '../../../utils/hooks/useActions';
import useLocalize from '../../../utils/hooks/useLocalize';
import useNewsArticleModal from '../../../utils/hooks/useNewsArticleModal';
import { newsProvider } from '../../../utils/newsProvider';
import DateTime from '../../common/DateTime';
import NewsArticleModal from '../../common/NewsArticleModal';
import './NewsSidebarContainer.scss';

const NUM_NEWS_TO_FETCH = 250; // Fetch more than needed for filtering


/**
 * NewsSidebarContainer - Compact news headlines component for Symbol Details sidebar
 *
 * @param {Object} props Component props
 * @param {string} props.symbol Symbol identifier
 * @param {number} props.newsAmount Number of news articles to show (default: 10)
 * @returns {React.Component} News sidebar component
 */
const NewsSidebarContainer = ({ symbol, newsAmount = 10 }) => {
  const localize = useLocalize();

  // Actions
  const [getNews] = useActions([fetchHeadlines], []);

  // Custom hook for modal management
  const {
    open,
    selectedArticle,
    articlesRead,
    openArticle,
    closeModal
  } = useNewsArticleModal();

  // Redux Store
  const headlineData = useSelector(
    (state) => (state.news.headlines.data || {}).articles || []
  );
  const isLoading = useSelector((state) => state.news.isLoading || false);
  const isError = useSelector((state) => state.news.isError || false);

  // Fetch news on component mount or when symbol changes
  useEffect(() => {
    if (symbol) {
      getNews(symbol, NUM_NEWS_TO_FETCH);
    }
  }, [getNews, symbol]);

  // Render content based on state
  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return <div className="loading">{localize('news.article.loading')}</div>;
    }

    // Error state
    if (isError) {
      return <div className="no-news">{localize('message.status.error')}</div>;
    }

    // Get top N headlines
    const headlines = headlineData.slice(0, newsAmount);

    // No news state
    if (headlines.length === 0) {
      return (
        <div className="no-news">{localize('symbol.details.news.no.news')}</div>
      );
    }

    // Success state - render news articles
    return headlines.map((article, index) => {
      const isRead = articlesRead.includes(article.articleId);
      return (
        <div
          key={article.articleId || index}
          className="news-item"
          onClick={() => openArticle(article)}
        >
          <a className={`news-title ${isRead ? 'viewed' : ''}`}>{article.title}</a>
          <div className="news-metadata">
            <span className="news-timestamp">
              <DateTime
                value={article.timestamp}
                hasZeroPad={true}
                hasYear={true}
                format="YYYY/MM/DD HH:mm"
              />
            </span>
            <span className="news-provider">
              {localize(newsProvider(article.provider))}
            </span>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="news-sidebar-container">
      <div className="news-header">
        {localize('symbol.details.news.header.label')}
      </div>
      {renderContent()}
      <NewsArticleModal
        open={open}
        onClose={closeModal}
        article={selectedArticle}
        isWide={true}
      />
    </div>
  );
};

export default NewsSidebarContainer;